"use client";

import { useEffect, useRef, useState } from "react";

// The JavaScript MIME type essences from the WHATWG MIME Sniffing spec. A browser treats any other type as a data block.
const JAVASCRIPT_TYPES = new Set([
  "application/ecmascript",
  "application/javascript",
  "application/x-ecmascript",
  "application/x-javascript",
  "text/ecmascript",
  "text/javascript",
  "text/javascript1.0",
  "text/javascript1.1",
  "text/javascript1.2",
  "text/javascript1.3",
  "text/javascript1.4",
  "text/javascript1.5",
  "text/jscript",
  "text/livescript",
  "text/x-ecmascript",
  "text/x-javascript",
]);

// The browser fires neither load nor error for a script it ignores, so waiting on one never ends.
function willBrowserRun(script: HTMLScriptElement): boolean {
  if (script.noModule && "noModule" in HTMLScriptElement.prototype) {
    return false;
  }
  const type = (script.getAttribute("type") ?? "")
    .replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, "")
    .toLowerCase();
  return type === "" || type === "module" || JAVASCRIPT_TYPES.has(type);
}

// the difference between this component and normal __dangerouslySetHtml is that this executes any scripts inside
export default function DangerouslySetHtmlContent(props: {
  html: string;
  [key: string]: any;
}): React.ReactElement {
  const { html, ...rest } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const [useDangerouslySetInnerHtml, setUseDangerouslySetInnerHtml] =
    useState<boolean>(false);

  useEffect(() => {
    if (!html || !divRef.current) {
      return;
    }

    let cancelled = false;
    try {
      const fragment = document.createRange().createContextualFragment(html);
      // Inserted together, scripts run as they arrive, so inline code that calls an external SDK
      // runs before the SDK loads. Hold them back and run them one at a time in source order.
      const scripts = Array.from(fragment.querySelectorAll("script")).map(
        (script) => {
          const placeholder = document.createComment("");
          script.replaceWith(placeholder);
          return { script, placeholder };
        },
      );
      divRef.current.innerHTML = "";
      divRef.current.appendChild(fragment);
      (async () => {
        for (const { script, placeholder } of scripts) {
          if (cancelled) return;
          const runnable = document.createElement("script");
          for (const { name, value } of Array.from(script.attributes)) {
            runnable.setAttribute(name, value);
          }
          runnable.text = script.text;
          const loaded =
            runnable.src &&
            willBrowserRun(runnable) &&
            new Promise((resolve) => {
              runnable.addEventListener("load", resolve);
              runnable.addEventListener("error", resolve);
            });
          placeholder.replaceWith(runnable);
          await loaded;
        }
      })();
    } catch (e) {
      // This error happens when createContextualFragment() isn't supported in certain browsers
      if (e instanceof DOMException && e.code === 9) {
        console.error(
          "DOMException occurred, falling back to use dangerouslySetInnerHTML",
        );
        setUseDangerouslySetInnerHtml(true);
      } else {
        throw e; // re-throw the error unchanged
      }
    }
    return () => {
      cancelled = true;
    };
  }, [html]);

  return (
    <div
      {...rest}
      ref={divRef}
      suppressHydrationWarning
      dangerouslySetInnerHTML={
        useDangerouslySetInnerHtml ? { __html: html } : undefined
      }
    />
  );
}
